import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    fetchPosts(currentPage);
  }, [currentPage]);

  const fetchPosts = async (page = 1) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/blog/posts?page=${page}&limit=6`
      );

      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts);
        setPagination(data.pagination);
      } else {
        setError('Failed to fetch blog posts');
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      setError('Network error. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const truncateText = (text, maxLength = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading && posts.length === 0) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading blog posts...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Blog - WeFlare"
        description="Read the latest updates, stories, and insights from WeFlare's youth empowerment programs in Africa."
        keywords="WeFlare blog, youth empowerment, Africa, education, health, community development, success stories"
      />
      
      {/* JSON-LD Structured Data for Blog Listing */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "WeFlare Blog",
          "description": "Updates and stories from WeFlare's youth empowerment programs in Africa",
          "url": `${window.location.origin}/blog`,
          "publisher": {
            "@type": "Organization",
            "name": "WeFlare",
            "logo": {
              "@type": "ImageObject",
              "url": `${window.location.origin}/logo.png`
            }
          },
          "blogPost": posts.map(post => ({
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.excerpt,
            "url": `${window.location.origin}/blog/${post.slug}`,
            "datePublished": post.published_at,
            "author": {
              "@type": "Person",
              "name": post.author_name || "WeFlare Team"
            },
            "keywords": post.tags ? post.tags.join(', ') : ''
          }))
        })}
      </script>
      
      <div className="container py-5">
        {/* Header */}
        <div className="text-center mb-5">
          <div className="mb-3">
            <span className="badge bg-primary-light text-primary px-3 py-2 rounded-pill">
              <i className="bi bi-journal-text me-2"></i>
              Our Blog
            </span>
          </div>
          <h1 className="text-display mb-4" style={{fontSize: 'clamp(2rem, 4vw, 3rem)', color: 'var(--gray-800)'}}>
            Stories of Impact
          </h1>
          <p className="lead text-muted mb-4" style={{maxWidth: '600px', margin: '0 auto'}}>
            Discover the latest updates from our programs, success stories from the youth we serve, 
            and insights into community development across Africa.
          </p>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {/* Blog Posts Grid */}
        {posts.length === 0 && !loading ? (
          <div className="text-center py-5">
            <i className="bi bi-journal-x fs-1 text-muted mb-3"></i>
            <h4 className="text-muted">No blog posts found</h4>
            <p className="text-muted">Check back soon for new content!</p>
          </div>
        ) : (
          <>
            <div className="row g-4 mb-5">
              {posts.map((post) => (
                <div key={post.id} className="col-lg-4 col-md-6">
                  <Link 
                    to={`/blog/${post.slug}`} 
                    className="text-decoration-none"
                    style={{ color: 'inherit' }}
                  >
                    <article className="card h-100 shadow-sm border-0 card-hover" style={{ cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>
                      {/* Featured Image */}
                      {post.featured_image && (
                        <div className="card-img-top-wrapper" style={{ height: '200px', overflow: 'hidden' }}>
                          <img
                            src={post.featured_image}
                            alt={post.title}
                            className="card-img-top"
                            style={{ 
                              width: '100%', 
                              height: '100%', 
                              objectFit: 'cover',
                              transition: 'transform 0.3s ease'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      
                      <div className="card-body d-flex flex-column">
                        {/* Tags */}
                        {post.tags && post.tags.length > 0 && (
                          <div className="mb-2">
                            {post.tags.slice(0, 2).map((tag, index) => (
                              <span key={index} className="badge bg-primary-light text-primary me-1">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {/* Title */}
                        <h5 className="card-title" style={{ lineHeight: '1.3', color: 'var(--bs-dark)' }}>
                          {post.title}
                        </h5>
                        
                        {/* Excerpt */}
                        <p className="card-text text-muted flex-grow-1">
                          {post.excerpt || truncateText(post.content.replace(/<[^>]*>/g, ''))}
                        </p>
                        
                        {/* Meta Info */}
                        <div className="d-flex justify-content-between align-items-center mt-auto pt-3 border-top">
                          <small className="text-muted">
                            <i className="bi bi-person me-1"></i>
                            {post.author_name || 'WeFlare Team'}
                          </small>
                          <small className="text-muted">
                            <i className="bi bi-calendar me-1"></i>
                            {formatDate(post.published_at)}
                          </small>
                        </div>
                        
                        {/* View Count */}
                        {post.view_count > 0 && (
                          <div className="mt-2">
                            <small className="text-muted">
                              <i className="bi bi-eye me-1"></i>
                              {post.view_count} views
                            </small>
                          </div>
                        )}
                      </div>
                    </article>
                  </Link>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <nav aria-label="Blog pagination">
                <ul className="pagination justify-content-center">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <i className="bi bi-chevron-left"></i>
                      Previous
                    </button>
                  </li>
                  
                  {[...Array(pagination.pages)].map((_, index) => {
                    const page = index + 1;
                    return (
                      <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                        <button 
                          className="page-link"
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      </li>
                    );
                  })}
                  
                  <li className={`page-item ${currentPage === pagination.pages ? 'disabled' : ''}`}>
                    <button 
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === pagination.pages}
                    >
                      Next
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </>
        )}

        {/* Call to Action */}
        <div className="text-center mt-5 pt-5 border-top">
          <h3 className="mb-3">Stay Updated</h3>
          <p className="text-muted mb-4">
            Don't miss our latest stories and program updates. Follow us on social media or subscribe to our newsletter.
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Link to="/donate" className="btn btn-primary">
              <i className="bi bi-heart me-2"></i>
              Support Our Mission
            </Link>
            <Link to="/about" className="btn btn-outline-primary">
              <i className="bi bi-info-circle me-2"></i>
              Learn More About Us
            </Link>
          </div>
        </div>
      </div>

      {/* Custom CSS for card hover effects */}
      <style>
        {`
        .card-hover:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
        }
        
        .card-hover:hover .card-img-top {
          transform: scale(1.05);
        }
        
        .card-hover {
          transition: all 0.3s ease;
        }
        
        .card-hover:hover .card-title {
          color: var(--bs-primary) !important;
        }
        `}
      </style>
    </>
  );
};

export default BlogPage;